import { FormEvent, useEffect, useState } from 'react'
import { Link, router, useForm } from '@inertiajs/react'
import { CheckCircle2, CreditCard, LockKeyhole, RotateCcw } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Page, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'

type OrderLineItem = {
  id: string
  code: string
  description: string
  service_fee_cents: number
  government_fee_cents: number
  total_cents: number
  term_months: number | null
  government_remittance: { state: 'queued' | 'awaiting_submission' | 'in_progress' | 'failed' | 'remitted' } | null
  assessed_amounts: {
    assessed: boolean
    tax_due_cents: number
    government_filing_fee_cents: number
    penalty_cents: number
    interest_cents: number
    credits_cents: number
    basis_summary: string | null
  }
}

const MAX_CONFIRMATION_CHECKS = 6

const REMITTANCE_LABELS: Record<NonNullable<OrderLineItem['government_remittance']>['state'], string> = {
  queued: 'Government amount queued for remittance',
  awaiting_submission: 'Government remittance is being prepared',
  in_progress: 'Government remittance is in progress',
  failed: 'Corppy is reviewing a failed government remittance. No action is needed unless we contact you.',
  remitted: 'Government amount remitted',
}

type OrderPayment = {
  total_cents: number
  paid: boolean
  paid_at: string | null
  invoice_number: string
  receipt_number: string | null
  refunded_cents: number
  fully_refunded: boolean
}

type Props = {
  order: {
    id: string
    state: 'unpaid' | 'refunded' | 'ready' | 'in_progress' | 'completed'
    payment_bypass_enabled: boolean
    business: { id: string; name: string }
    next_path: string
    next_label: string
    submitted_at: string
    currency: string
    line_items: OrderLineItem[]
    payment: OrderPayment
  }
  confirming_payment?: boolean
}

export default function OrderPaymentShow({ order, confirming_payment = false }: Props) {
  const basePath = useBasePath()
  const form = useForm({})
  const [confirmationChecks, setConfirmationChecks] = useState(0)
  const paymentCancelled = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('payment') === 'cancel'
  const paymentIsConfirming = confirming_payment && !order.payment.paid && !order.payment.fully_refunded

  useEffect(() => {
    if (!paymentIsConfirming || confirmationChecks >= MAX_CONFIRMATION_CHECKS) return

    const timer = window.setTimeout(() => {
      router.reload({
        only: ['order'],
        onFinish: () => setConfirmationChecks((current) => current + 1),
      })
    }, 3000)

    return () => window.clearTimeout(timer)
  }, [confirmationChecks, paymentIsConfirming])

  const checkPaymentAgain = () => {
    setConfirmationChecks(0)
    router.reload({
      only: ['order'],
      onFinish: () => setConfirmationChecks(1),
    })
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.post(`${basePath}/orders/${order.id}/payment`)
  }
  const paymentTitle = order.payment.fully_refunded ? 'Order refunded' : order.payment.paid ? 'Payment complete' : 'One order. One payment.'
  const paymentBadge = order.payment.fully_refunded ? 'Refunded' : order.payment.paid ? 'Paid' : 'Payment required'
  const paymentSubtitle = order.payment.fully_refunded
    ? 'The refunded order and its original charges are listed below.'
    : order.payment.paid
      ? 'Your receipt and complete order details are listed below.'
      : 'Everything Corppy will handle is listed below before you pay.'

  return (
    <DashboardLayout
      title={`Checkout — ${order.business.name}`}
      breadcrumbs={[
        { label: 'Businesses', href: 'businesses' },
        { label: order.business.name, href: `businesses/${order.business.id}` },
        { label: 'Checkout' },
      ]}
    >
      <Page>
        <PageHeader
          title={paymentTitle}
          badge={<Badge variant={order.payment.fully_refunded ? 'destructive' : order.payment.paid ? 'default' : 'secondary'}>{paymentBadge}</Badge>}
          subtitle={paymentSubtitle}
        />

        {paymentCancelled && !order.payment.paid && (
          <Card className="border-dashed shadow-none">
            <CardHeader>
              <CardTitle className="text-base">Checkout cancelled</CardTitle>
              <CardDescription>No charge was made. You can continue whenever you are ready.</CardDescription>
            </CardHeader>
          </Card>
        )}

        {paymentIsConfirming && (
          <Card className="border-dashed shadow-none" role="status" aria-live="polite">
            <CardHeader>
              <CardTitle className="text-base">
                {confirmationChecks >= MAX_CONFIRMATION_CHECKS ? 'Payment confirmation is taking longer' : 'Confirming your payment'}
              </CardTitle>
              <CardDescription>
                {confirmationChecks >= MAX_CONFIRMATION_CHECKS
                  ? 'No additional charge will be made. Check again safely, or return later to see the updated receipt.'
                  : 'Corppy is checking Stripe automatically. You can leave this page and return later without creating another charge.'}
              </CardDescription>
              {confirmationChecks >= MAX_CONFIRMATION_CHECKS && (
                <CardAction>
                  <Button type="button" variant="outline" size="sm" onClick={checkPaymentAgain}>
                    Check again
                  </Button>
                </CardAction>
              )}
            </CardHeader>
          </Card>
        )}

        {order.payment_bypass_enabled && !order.payment.paid && (
          <Card className="border-dashed shadow-none">
            <CardHeader>
              <CardTitle className="text-base">Development payment bypass</CardTitle>
              <CardDescription>
                This records a simulated successful payment for local testing. No card will be charged.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Order summary</CardTitle>
            <CardDescription>{order.business.name}</CardDescription>
            <CardAction>
              {order.payment.fully_refunded
                ? <RotateCcw className="size-5 text-destructive" aria-label="Refunded" />
                : order.payment.paid
                ? <CheckCircle2 className="size-5 text-primary" aria-label="Paid" />
                : <LockKeyhole className="size-5 text-muted-foreground" aria-label="Secure checkout" />}
            </CardAction>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              {order.line_items.map((lineItem) => (
                <div key={lineItem.id} className="flex items-start justify-between gap-6">
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-medium">{lineItem.description}</p>
                    {(lineItem.government_fee_cents > 0 || lineItem.term_months) && (
                      <p className="text-xs text-muted-foreground">
                        {lineItem.government_fee_cents > 0
                          ? `${formatCurrency(lineItem.government_fee_cents)} assessed government amount`
                          : null}
                        {lineItem.government_fee_cents > 0 && lineItem.term_months ? ' · ' : null}
                        {lineItem.term_months ? `${lineItem.term_months} months` : null}
                      </p>
                    )}
                    {lineItem.assessed_amounts.assessed && (
                      <div className="space-y-0.5 text-xs text-muted-foreground">
                        {lineItem.assessed_amounts.basis_summary && <p>{lineItem.assessed_amounts.basis_summary}</p>}
                        <p>Tax due: {formatCurrency(lineItem.assessed_amounts.tax_due_cents)}</p>
                        <p>Government filing fee: {formatCurrency(lineItem.assessed_amounts.government_filing_fee_cents)}</p>
                        <p>Penalties: {formatCurrency(lineItem.assessed_amounts.penalty_cents)}</p>
                        <p>Interest: {formatCurrency(lineItem.assessed_amounts.interest_cents)}</p>
                        <p>Credits and prior payments: {lineItem.assessed_amounts.credits_cents > 0 ? `−${formatCurrency(lineItem.assessed_amounts.credits_cents)}` : formatCurrency(0)}</p>
                        <p>Corppy service fee: {formatCurrency(lineItem.service_fee_cents)}</p>
                      </div>
                    )}
                    {order.payment.paid && !order.payment.fully_refunded && lineItem.government_remittance && (
                      <p className={lineItem.government_remittance.state === 'failed' ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'} role="status">
                        {REMITTANCE_LABELS[lineItem.government_remittance.state]}
                      </p>
                    )}
                  </div>
                  <p className="shrink-0 text-sm">
                    {lineItem.total_cents === 0 ? 'Included' : formatCurrency(lineItem.total_cents)}
                  </p>
                </div>
              ))}
            </div>
            <Separator />
            <div className="flex items-baseline justify-between gap-6">
              <p className="text-sm font-medium">Total</p>
              <p className="text-xl font-semibold">{formatCurrency(order.payment.total_cents)}</p>
            </div>
            <p className="text-xs text-muted-foreground">
              One payment covers every item listed in this order. No separate service checkouts.
            </p>
            {order.payment.paid && (
              <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                <p>Invoice {order.payment.invoice_number}</p>
                {order.payment.receipt_number && <p>Receipt {order.payment.receipt_number}</p>}
              </div>
            )}
            {order.payment.refunded_cents > 0 && (
              <p className="text-sm text-muted-foreground">
                Refunded {formatCurrency(order.payment.refunded_cents)}
              </p>
            )}
          </CardContent>
          <CardFooter className="border-t">
            {order.payment.paid ? (
              <Button asChild>
                <Link href={order.next_path}>{order.next_label}</Link>
              </Button>
            ) : (
              <form onSubmit={submit} className="w-full">
                <Button type="submit" disabled={form.processing} className="w-full" aria-busy={form.processing}>
                  <CreditCard className="size-4" aria-hidden="true" />
                  {form.processing
                    ? order.payment_bypass_enabled ? 'Completing test payment…' : 'Opening checkout…'
                    : order.payment_bypass_enabled ? 'Complete test payment' : `Pay ${formatCurrency(order.payment.total_cents)}`}
                </Button>
              </form>
            )}
          </CardFooter>
        </Card>
      </Page>
    </DashboardLayout>
  )
}
